import type { ReportContent, ReportLayoutPolicy, ReportLetterhead } from '../lib/api/reports.gen';
import { DEFAULT_REPORT_LAYOUT, LETTERHEAD_CONFIDENTIALITY_LABELS } from '../lib/agentShared/formSchema';
import { REPORT_PAGE_SIZE_MM } from './components/reportPageFormat';

// paginate.ts — THE single pagination adapter (story 4-128b; P06 §4.3's own
// migration table: "Own the single Paged.js adapter and generated `@page`/
// running-element rules for all letterhead modes, covers, body-page numbering
// and TOC. Consume shared policy data; neither host reconstructs the CSS.
// Return page count, anchor→page mapping, diagnostics and final readiness.").
//
// ── What moved here, and what did not ──────────────────────────────────────
// Before this story every line below lived inside
// `modules/reports/components/PagedReportPreview.tsx`, interleaved with React
// state, a Skeleton, a Callout, portals and a debounce — so the ONLY way for a
// second host (the worker, `4-128e`) to get the same paper was to re-derive
// the same rules by hand, which is exactly how the retired `pdfExport.service.js` and
// `pagedLetterheadRenderer.service.js` ended up as two more copies.
//
// This module is a PURE function of `ReportContent` plus a DOM the caller
// already paginated. It imports no React, no `pagedjs`, no app CSS and no app
// state. `PagedReportPreview.tsx` remains the interactive HOST: it owns the
// Previewer instance, the debounce, the loading/error chrome, the badge
// portals and the page shadows — none of which are paper. `4-128d` moves that
// host into an isolated iframe; nothing in THIS file has to change when it
// does, which is the point of the split.

/**
 * The vertical band a repeating letterhead reserves at the top of every
 * physical page, in millimetres.
 *
 * Story 4-128b — THE frontend's single source. Before this story the same
 * literal `32` was hand-written in **two** frontend files
 * (`PagedReportPreview.tsx` and `components/ui/reportLayoutPolicy.ts`) and, per
 * P06 §4.3's own migration inventory, at least two more times on the backend
 * (`services/pdf/reportLayoutPolicy.js`, consumed by
 * `pagedLetterheadRenderer.service.js`). Preview and PDF agreeing on page count
 * and break locations is *literally* this constant agreeing, so four
 * independently-editable copies of it is the defect shape, not the pattern.
 *
 * Backend is untouched by this child (its own explicit constraint). The two
 * FRONTEND copies are collapsed into this one; `reportLayoutPolicy.ts` now
 * imports it from here. `4-128e` is the child that makes the worker consume
 * the shared artifact, at which point the backend copies stop being twins and
 * start being dead code for `4-128f` to delete.
 *
 * The worst-case slot-height math this value is sized against is documented on
 * `reportLayoutPolicy.js#LETTERHEAD_RESERVE_MM` (story 4-128f: this used to be
 * described as "the backend twin"; there is one renderer now, and this is the
 * one place the reserve reaches paper from); the value
 * is kept as the SAME literal here rather than re-derived, deliberately.
 */
export const LETTERHEAD_RESERVE_MM = 32;

/**
 * The default margin box used when a `ReportContent` predates
 * `layoutPolicy` (a frozen `assembledContent` snapshot). Byte-identical to the
 * `normal` preset in `reportLayoutPolicy.ts#MARGIN_PRESETS_MM` — the same
 * fallback `PagedReportPreview` inlined before this story.
 */
const FALLBACK_MARGINS_MM = { top: 16, right: 14, bottom: 20, left: 14 };

/**
 * Paged.js mis-measures a cover page sized to EXACTLY the content-area height
 * (0.004px apart, sub-pixel rounding in the mm→px conversion) and splits it,
 * producing a real, reproduced spurious blank front-matter page. 2mm of slack,
 * not the exact boundary value. Live-found; see the rule's own comment below.
 */
const COVER_HEIGHT_SAFETY_MM = 2;

export interface ReportPageGeometry {
  /** Physical page width in mm, orientation already applied. */
  widthMm: number;
  /** Physical page height in mm, orientation already applied. */
  heightMm: number;
  /** The real `@page` margin box, letterhead reserve already added to `top`. */
  marginsMm: { top: number; right: number; bottom: number; left: number };
  /** Whether `letterhead.mode` calls for per-page repetition. */
  repeatingLetterhead: boolean;
  /** The page's own usable content height, minus the cover safety slack. */
  contentHeightMm: number;
}

/**
 * Resolve the physical page geometry for a report, from the shared layout
 * policy alone. Extracted verbatim from `PagedReportPreview#buildPagination
 * Stylesheet` so the numbers and the CSS that spends them can no longer drift.
 */
export function resolvePageGeometry(content: ReportContent): ReportPageGeometry {
  const pageFormat = content.style.pageSize === 'letter' ? 'letter' : 'A4';
  const landscape = content.style.pageOrientation === 'landscape';
  // Story 4-114 — orientation swaps which physical dimension is width vs
  // height; explicit numbers below (not the CSS `landscape` keyword) so this
  // stays consistent with `content.layoutPolicy`'s own already-swapped
  // `marginsMm`/`pageFormat` resolution (`reportLayoutPolicy.ts`).
  const rawSize = REPORT_PAGE_SIZE_MM[pageFormat];
  const widthMm = landscape ? rawSize.height : rawSize.width;
  const heightMm = landscape ? rawSize.width : rawSize.height;
  const margins = content.layoutPolicy?.marginsMm ?? FALLBACK_MARGINS_MM;
  // Story 4-122 — when the letterhead repeats on every page, EVERY page
  // (including the cover) reserves an extra top-margin band for it — the same
  // widened-@page-margin approach the retired backend renderer used, so preview
  // and PDF agree on how much vertical room a repeating letterhead consumes per
  // page (and therefore on page count/break locations for the same template).
  // Since story 4-128f "agree" is structural rather than maintained: the PDF is
  // serialized from this very rule.
  // `ReportLetterhead` rendering nothing (mode `'none'`, or every slot
  // disabled) means `.report-letterhead` doesn't exist in the DOM at all — the
  // `position: fixed` rule below then matches nothing, so this reserve would be
  // pure wasted margin for a template with nothing to repeat; only widen it
  // when the mode actually calls for repetition.
  const repeatingLetterhead = content.layoutPolicy?.letterhead?.mode === 'everyContentPage';
  const topMarginMm = margins.top + (repeatingLetterhead ? LETTERHEAD_RESERVE_MM : 0);
  return {
    widthMm,
    heightMm,
    marginsMm: { ...margins, top: topMarginMm },
    repeatingLetterhead,
    contentHeightMm: heightMm - topMarginMm - margins.bottom - COVER_HEIGHT_SAFETY_MM,
  };
}

// ── The running footer (story 4-128e, defect D-3) ─────────────────────────
//
// Three shipped controls — `letterhead.footerText`, `letterhead.
// footerPageNumber` and `letterhead.confidentiality` (visible through a slot's
// `{confidential}` placeholder) — used to resolve EXCLUSIVELY through
// Puppeteer's `displayHeaderFooter`/`footerTemplate` frame
// (the retired `pdfExport.service.js#buildFooterTemplate`). Neither frontend host rendered
// a footer at all, which is `defects.md`'s **D-3**.
//
// P06 §4.3 closes that off from both ends at once: the shared serialization
// path it mandates is `page.pdf` with `displayHeaderFooter: false` and "Do not
// use Puppeteer's separate header/footer template frames", and D-3's own
// "Correct behaviour" names the replacement — "`paginate.ts` owns running
// header/footer placement through real CSS Paged Media margin boxes, shared by
// all hosts. Page numbers come from the same final pagination the TOC uses."
// So the moment `4-128e` turns that flag off, the footer has to exist HERE or
// it does not exist anywhere, and every customer using a footer slot silently
// loses it. This is that implementation.
//
// Real margin boxes rather than a `position: fixed` clone (the mechanism the
// repeating LETTERHEAD uses above), for one decisive reason: `{page}`/`{total}`.
// A cloned element is the same element on every page and cannot carry a
// per-page number; `@bottom-right { content: counter(page) " of " counter(pages) }`
// is resolved by the fragmenter per page, from the same final pagination the
// TOC numbers come from. It also removes the whole class of limitation 4-88a
// recorded on `ReportLetterhead.mode` — Chromium's header/footer frame paints
// no raster images and ignores the printed document's own stylesheet.
//
// Physical placement is unchanged from the retired template: the boxes live in
// the `@page` bottom margin band, `@bottom-left` starting at the content-left
// edge and `@bottom-right` ending at the content-right edge — the same two
// ends the old `display:flex; justify-content:space-between; padding:0 14mm`
// frame resolved to. They consume NO content height (a margin box is in the
// margin), so this cannot move a page break.

/** A slot's own `content`, trimmed, or `fallback` when blank/absent. Mirrors
 * the retired `pdfExport.service.js#slotContentOr` exactly — the resolution
 * moved here in 4-128e and its original was deleted in 4-128f; one owner. */
const slotContentOr = (
  slot: { enabled: boolean; content: string | null } | null | undefined,
  fallback: string
): string => (slot?.content && slot.content.trim() ? slot.content : fallback);

const humaniseStatus = (value: string | null | undefined): string => String(value || '').replace(/_/g, ' ');

/**
 * The footer's short date.
 *
 * The retired `pdfExport.service.js` called this "locale-agnostic" and used a bare
 * `toLocaleDateString()`. It was not: that resolves against the HOST's default
 * locale, which in the worker container is whatever ICU defaults to and in a
 * template author's browser is their own. Two hosts, two answers, for a value
 * P06 §4.1 requires to be identical. Pinned to `en-US` here, which is exactly
 * what the worker's own container already resolved — so the generated PDF's
 * footer text is unchanged and the PREVIEW now agrees with it instead of
 * drifting per reader.
 *
 * Story 4-130 pinned the TIME ZONE too (defect D-14). The locale fix above was
 * only half of it: `toLocaleDateString` still resolves against the host's own
 * zone, so a timestamp within ten hours of midnight prints a different DATE in
 * a Brisbane browser and a UTC worker — the same divergence 4-130's matrix
 * caught in `ReportMediaSummaryGrid#formatCapturedAt`, which prints an hour and
 * therefore showed it on every photo instead of on one day in twelve. See that
 * function for why UTC rather than a tenant zone.
 */
const footerShortDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { timeZone: 'UTC' });
};

/**
 * Story 4-115's placeholder vocabulary, moved verbatim from
 * the retired `pdfExport.service.js#footerPlaceholderValues`. No escaping happens here —
 * the destination is a CSS `<string>` literal, not HTML, and `cssString` below
 * owns that escape. Every value is plain text from validated content; no user
 * markup ever becomes executable document input (P06 §4.3).
 */
const footerPlaceholderValues = (
  content: ReportContent,
  letterhead: ReportLetterhead | null
): Record<string, string> => ({
  site: content.subjectName || '',
  status: humaniseStatus(content.inspectionStatus),
  inspectionDate: footerShortDate(content.inspectionDate),
  generatedAt: footerShortDate(content.generatedAt),
  reportReference: content.reportReference || '',
  confidential:
    letterhead?.confidentiality && letterhead.confidentiality !== 'none'
      ? LETTERHEAD_CONFIDENTIALITY_LABELS[letterhead.confidentiality] || ''
      : '',
});

/** A CSS `<string>` literal. Backslash and the delimiter are the only two
 * characters that may not appear raw; a newline would terminate it. */
const cssString = (value: string): string =>
  `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\r\n]+/g, ' ')}"`;

/**
 * Build one margin box's `content` VALUE from a slot's format string.
 *
 * Value placeholders (`{site}`, `{confidential}`, …) are plain substitutions,
 * identical to the retired `applyFooterPlaceholders`. `{page}`/`{total}` are
 * not — they are the two that cannot be text, because the number is per page.
 * They become `counter(page)`/`counter(pages)`, which is why the result is a
 * CONCATENATION of quoted strings and counters rather than a single string.
 */
const footerContentValue = (
  format: string,
  values: Record<string, string>,
  allowCounters: boolean
): string => {
  let text = format;
  for (const [token, value] of Object.entries(values)) text = text.split(`{${token}}`).join(value);
  if (!allowCounters) return cssString(text);
  const parts: string[] = [];
  const pattern = /\{(page|total)\}/g;
  let cursor = 0;
  let match = pattern.exec(text);
  while (match) {
    if (match.index > cursor) parts.push(cssString(text.slice(cursor, match.index)));
    parts.push(match[1] === 'page' ? 'counter(page)' : 'counter(pages)');
    cursor = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (cursor < text.length) parts.push(cssString(text.slice(cursor)));
  return parts.length > 0 ? parts.join(' ') : '""';
};

/**
 * The typography of both footer boxes. Values carried over verbatim from the
 * retired `buildFooterTemplate`'s inline style so the printed footer does not
 * change size or colour in this migration — with ONE deliberate correction:
 * the font stack. The old frame could not see the document's stylesheet and
 * hard-coded `system-ui, -apple-system, 'Segoe UI', sans-serif` — the same
 * substitution defect D-1 records for the body. A margin box IS in the
 * document, so it inherits the packaged report face like everything else.
 */
const FOOTER_BOX_STYLE = `
      font-family: inherit;
      font-size: 8px;
      line-height: 1.2;
      color: #64748b;`;

/**
 * The `@page` margin-box declarations for the two footer slots, or `''` when
 * neither is enabled — in which case no `@page` margin box is emitted at all
 * and a template that never opened the letterhead panel gets exactly the paper
 * it got before this story.
 */
export function buildRunningFooterMarginBoxes(content: ReportContent): string {
  const letterhead = (content.style?.letterhead ?? null) as ReportLetterhead | null;
  const textOn = Boolean(letterhead?.footerText?.enabled);
  const pageNumberOn = Boolean(letterhead?.footerPageNumber?.enabled);
  if (!textOn && !pageNumberOn) return '';
  const values = footerPlaceholderValues(content, letterhead);
  const textValue = footerContentValue(
    slotContentOr(letterhead?.footerText, DEFAULT_REPORT_LAYOUT.footerText),
    values,
    false
  );
  // The same default format every enabled page-number slot showed out of the
  // box before this story (`buildFooterTemplate`'s own literal).
  const pageNumberValue = footerContentValue(
    slotContentOr(letterhead?.footerPageNumber, 'Page {page} of {total}'),
    values,
    true
  );
  return `${
    textOn
      ? `
      @bottom-left {
        content: ${textValue};${FOOTER_BOX_STYLE}
      }`
      : ''
  }${
    pageNumberOn
      ? `
      @bottom-right {
        content: ${pageNumberValue};${FOOTER_BOX_STYLE}
      }`
      : ''
  }`;
}

/**
 * THE generated pagination stylesheet: the `@page` rule, the running-element
 * placement for a repeating letterhead, and the fragmentation rules that only
 * make sense once a real fragmenter is running. Moved verbatim from
 * `PagedReportPreview#buildPaginationStylesheet` (story 4-128b) — every rule,
 * comment and literal is unchanged, because this child's own done-when forbids
 * moving a pixel.
 *
 * Why these rules are NOT in `document.css`: they are the only report rules
 * whose VALUES depend on the resolved per-template policy (page size,
 * orientation, margin preset, letterhead mode), so they cannot be a static
 * stylesheet. `document.css` owns every static paper rule; this function owns
 * every generated one. That is the whole ownership split — two files, no third
 * place, and neither host reconstructs the other's half.
 */
export function buildPaginationStylesheet(content: ReportContent): string {
  const geometry = resolvePageGeometry(content);
  const { marginsMm: margins, repeatingLetterhead } = geometry;
  // The page's own usable CONTENT height — physical height minus the real
  // `@page` margin — is what a single page can actually hold. Purely a
  // continuous-scroll target in every OTHER caller of `ReportCoverPage`
  // (there, "one physical page" and "the div's own box" are the same number —
  // no `@page` margin concept exists outside pagination), so it is overridden
  // here, and ONLY here, rather than changed at the source.
  const contentHeightMm = geometry.contentHeightMm.toFixed(2);
  // `resolvePageGeometry` already added the letterhead reserve onto
  // `margins.top`; the running-element clone below needs the UNWIDENED top so
  // it sits in the band the reserve created, not below it.
  const letterheadTopMm = margins.top - (repeatingLetterhead ? LETTERHEAD_RESERVE_MM : 0);
  return `
    @page {
      size: ${geometry.widthMm}mm ${geometry.heightMm}mm;
      margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;${buildRunningFooterMarginBoxes(
        content
      )}
    }
    ${
      repeatingLetterhead
        ? `
    /* Story 4-122 — Paged.js's PositionFixed handler pulls any source
       position:fixed element out of flow and clones it into every physical
       page (mechanism and wrinkles verified live against this product's own
       multi-logo/title/address letterhead markup; story 4-128f deleted the
       backend copy of this comment along with the renderer it described). top/left/right are
       page-EDGE coordinates (the clone is absolute relative to the full
       page box, not the printable area) — left/right match the real
       content margins so it lines up with the column below it, same as the
       single, non-repeating render today. Gated behind repeatingLetterhead
       (mode === 'everyContentPage') — real bug caught before shipping:
       applying position:fixed unconditionally would ALSO repeat the
       letterhead for 'firstContentPage' (the default, most common mode)
       whenever ANY slot is enabled, silently disagreeing with the backend
       (which only repeats for 'everyContentPage') and with this component's
       OWN single-render-once behavior for that mode a moment ago. */
    .report-letterhead {
      position: fixed;
      top: ${letterheadTopMm}mm;
      left: ${margins.left}mm;
      right: ${margins.right}mm;
    }
    /* Story 4-122 — a live-found wrinkle: PositionFixed
       REMOVES .report-letterhead from the flow entirely (only per-page clones
       survive), so a cover page's real next sibling becomes the plain
       <header> once the letterhead node is gone, and IT must carry the
       "start a fresh page" break pageBreakBefore puts on the
       (about-to-be-removed) letterhead node instead. */
    .report-cover-page + header { break-before: page; }
    `
        : ''
    }
    /* Real bug, found live: ReportCoverPage's own 'flex flex-col
       justify-between' layout (byte-identical to the backend PDF's own
       .report-cover-page CSS — this is deliberately the SAME visual
       recipe, not a divergence) is a KNOWN weak spot for CSS Fragmentation:
       Paged.js's layout algorithm measures box heights assuming normal block
       flow, and a flex 'justify-content: space-between' child's real
       position depends on the container's TOTAL height, which the
       fragmenter cannot know in advance — it mis-measured this box as
       overflowing (even with a height comfortably under one page's content
       area AND 'break-inside: avoid' set) and split it, stranding the
       trailing decorative rule alone on a second, otherwise-blank
       front-matter page. Real Chromium print (what the downloaded PDF uses)
       does not have this bug — this is specific to Paged.js's own
       fragmentation engine, not a general flexbox problem. Fix: neutralize
       flex for JUST this adapter's own copy of the markup and reproduce the
       same "centered content, rule pinned near the bottom" look with
       absolute positioning instead — which contributes NO extra height to
       the parent's own box, so Paged.js's fragmenter has nothing to
       mis-measure. ReportCoverPage.tsx itself is untouched; this is scoped
       to .report-paged-preview's own stylesheet only. */
    .report-cover-page {
      height: ${contentHeightMm}mm !important;
      min-height: 0 !important;
      display: block !important;
      position: relative;
      overflow: hidden;
      break-inside: avoid;
    }
    .report-cover-page > div:nth-child(2) {
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      transform: translateY(-50%);
    }
    .report-cover-page > div:last-child {
      position: absolute;
      bottom: 12mm;
      left: 50%;
      transform: translateX(-50%);
    }
    .report-section { break-inside: avoid; }
    .report-section-heading { break-after: avoid; }
    .report-page-break { break-before: page; }
    /* Story 4-110 — per-section pageBreak's 'avoid' value
       (ReportDocument.tsx's own header comment has the full mechanism); kept
       in lockstep with document.css's identical
       rule so this live preview never disagrees with the real print/PDF
       output over which sections glue to the one before them. */
    .report-break-avoid { break-before: avoid; }
    .report-page-break-marker { display: none; }
  `;
}

// ── Front matter, declared rather than sniffed (story 4-128b, defect D-5/D-6) ──

/**
 * How many LEADING physical pages are front matter — pages that exist but do
 * not consume a body page number.
 *
 * **This is the D-6 fix.** (`docs/prd/reference/4-128-report-renderer/
 * defects.md`, "The cover page consumes a body page number when the letterhead
 * repeats".)
 *
 * Before this story `PagedReportPreview` implemented P06's "front matter does
 * not consume a body page number" by searching the paginated DOM for the first
 * `.pagedjs_page` containing `[data-report-body-start]`. That marker lives on
 * `.report-letterhead` whenever a header slot is enabled — and with
 * `letterhead.mode: 'everyContentPage'`, Paged.js's own `PositionFixed`
 * handler CLONES `.report-letterhead`, marker included, onto every physical
 * page, cover included. `bodyStartIndex` therefore resolved to `0` and the
 * cover was numbered as body page 1. Two features that each work correctly
 * alone (4-108's front-matter exemption, 4-122's repeating letterhead) broke
 * each other, because the exemption was INFERRED from markup that a pagination
 * handler is free to duplicate.
 *
 * The fix defects.md names, implemented here: body-page numbering comes from
 * the shared layout policy's front-matter DECLARATION
 * (`ReportLayoutPolicy.frontMatter`, whose `countsTowardPageNumbers` field
 * already existed as the placeholder for exactly this) and is returned by
 * `paginate.ts` alongside the page count and anchor map — never re-derived
 * from the DOM.
 *
 * The cover page is one physical page by construction, not by assumption: the
 * generated stylesheet above clamps `.report-cover-page` to exactly one
 * content-area height with `break-inside: avoid`, and forces a `break-before:
 * page` onto whatever follows it. So "front matter is present" and "front
 * matter is one page" are the same fact here. A future multi-page front matter
 * is a policy field (`frontMatter.pageCount`), not another DOM search.
 */
export function resolveFrontMatterPageCount(policy: ReportLayoutPolicy | null | undefined): number {
  const frontMatter = policy?.frontMatter;
  if (!frontMatter?.enabled) return 0;
  // `countsTowardPageNumbers: true` means the cover IS body page 1 — a real
  // (if unusual) house style, and the reason this is a policy field rather
  // than a boolean "has cover".
  return frontMatter.countsTowardPageNumbers ? 0 : 1;
}

export interface PaginationResult {
  /** Every physical page fragment, in document order. */
  pageElements: HTMLElement[];
  /** Total physical pages — the number a PDF viewer's page indicator shows. */
  pageCount: number;
  /** Leading pages exempt from body numbering (see `resolveFrontMatterPageCount`). */
  frontMatterPageCount: number;
  /** The body pages, in order. `bodyPages[0]` is body page 1. */
  bodyPages: HTMLElement[];
  /**
   * `data-toc-key` → 1-based PHYSICAL page number (front matter INCLUDED —
   * the same number a PDF viewer's own indicator shows, deliberately distinct
   * from the body-page count above; see `ReportTableOfContents`'s own header
   * comment). A repeated section shares ONE `tocKey` across every instance;
   * the first matching node in document order wins.
   *
   * **This is the interface half of D-5.** `report.service.js
   * #tableOfContentsOf` currently builds its TOC entries from the V1 section
   * wrapper, which `agents/shared/formSchema.js#toV1` emits with no `id`, so
   * every entry key falls back to `section-<index>` while every rendered
   * anchor is the FormSchemaV2 section id — the keys can never match and every
   * PDF TOC entry resolves `page: null`. That root cause is NOT fixable from
   * the frontend (see `defects.md`'s D-5 entry, amended 2026-09-11, and this
   * story's own "backend untouched" constraint). What this child owns is the
   * CONTRACT the fix will consume: one map, keyed by the anchors the document
   * actually emits, produced by the SAME final pagination the body was laid
   * out by — never a second measurement pass whose keys can silently miss.
   */
  tocPageByKey: Map<string, number>;
  /**
   * `data-report-section-id` → 1-based physical page number. The general
   * anchor→page mapping P06 §4.3 requires the adapter to return; `tocPageByKey`
   * is the TOC-shaped view of the same pass.
   */
  anchorPageBySectionId: Map<string, number>;
}

/**
 * Read the final page/anchor map out of an already-paginated container.
 *
 * P06 §4.3: "Return page count, anchor→page mapping, diagnostics and final
 * readiness." This is the read half — the caller owns the engine, this owns
 * the interpretation, so a second host reading the same DOM cannot reach a
 * different answer about which anchor landed on which page.
 */
export function readPagination(container: HTMLElement, content: ReportContent): PaginationResult {
  const pageElements = Array.from(container.querySelectorAll<HTMLElement>('.pagedjs_page'));
  const indexOfPage = (el: Element | null): number =>
    el ? pageElements.indexOf(el as HTMLElement) : -1;

  const tocPageByKey = new Map<string, number>();
  container.querySelectorAll<HTMLElement>('[data-toc-key]').forEach((el) => {
    const key = el.getAttribute('data-toc-key');
    if (!key || tocPageByKey.has(key)) return;
    const idx = indexOfPage(el.closest('.pagedjs_page'));
    if (idx !== -1) tocPageByKey.set(key, idx + 1);
  });

  const anchorPageBySectionId = new Map<string, number>();
  container.querySelectorAll<HTMLElement>('[data-report-section-id]').forEach((el) => {
    const key = el.getAttribute('data-report-section-id');
    if (!key || anchorPageBySectionId.has(key)) return;
    const idx = indexOfPage(el.closest('.pagedjs_page'));
    if (idx !== -1) anchorPageBySectionId.set(key, idx + 1);
  });

  // Declared, not sniffed — see `resolveFrontMatterPageCount`. Clamped to the
  // real page count so a policy that declares front matter for a document that
  // somehow produced fewer pages can never yield a negative body count.
  const frontMatterPageCount = Math.min(
    resolveFrontMatterPageCount(content.layoutPolicy),
    pageElements.length
  );

  return {
    pageElements,
    pageCount: pageElements.length,
    frontMatterPageCount,
    bodyPages: pageElements.slice(frontMatterPageCount),
    tocPageByKey,
    anchorPageBySectionId,
  };
}

/**
 * Fill each `[data-toc-page-for]` slot with the real page number this
 * pagination pass resolved. Plain DOM text mutation, not a second React render
 * pass: this is leaf text, and it is the same "real DOM text once real layout
 * exists" rule `ReportPageBadge`'s own portal follows a step later.
 */
export function applyTocPageNumbers(container: HTMLElement, tocPageByKey: Map<string, number>): void {
  container.querySelectorAll<HTMLElement>('[data-toc-page-for]').forEach((el) => {
    const key = el.getAttribute('data-toc-page-for');
    const pageNumber = key ? tocPageByKey.get(key) : undefined;
    el.textContent = pageNumber != null ? String(pageNumber) : '';
  });
}
